/**
 * iter92 — EmissionActions
 *
 * Panneau d'émission de la facture (page "Facture à émettre").
 * 4 canaux : Caisse / Mail / Courrier / Hybride.
 *   - Mail : email manquant → modal de saisie (persisté sur la fiche client)
 *   - Courrier : téléchargement automatique du PDF nommé "<n° facture>.pdf"
 *   - Hybride : montant partiel encaissé en caisse + solde via Mail / Courrier
 */
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Banknote, Mail, Send, Loader2, Printer, Shuffle, UserCog, Search, X, Paperclip,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { downloadAuthBlob } from "@/lib/downloadPdf";

const ACTION_LABELS = {
  caisse: "Caisse", mail: "Mail", courrier: "Courrier", hybride: "Hybride",
};

const SECONDARY_OPTIONS = [
  { key: "mail", label: "Mail", Icon: Mail },
  { key: "courrier", label: "Courrier", Icon: Printer },
];

export default function EmissionActions({ orId, total, clientEmail, emailOptions = [], clientId, clientLabel, orTypeClientTarif, orImportMetadata, orNoSinistre, orNumero, beforeAction, onEmitted }) {
  const isAssurance = orTypeClientTarif === "assurance";
  // iter173 — sujet suggéré : "Facture <num> — N° sinistre <X>" si assurance & sinistre connu, sinon défaut
  const buildDefaultSubject = () => {
    const num = orNumero || "";
    if (isAssurance) {
      // iter233 — N° de sinistre saisi dans les en-têtes de l'OR, sinon import Auto-i
      const sinistre = orNoSinistre || orImportMetadata?.sinistre_numero;
      if (sinistre) return `Facture ${num} — N° sinistre ${sinistre}`;
      return `Facture ${num} — Sinistre à préciser`;
    }
    // iter268 — Aperçu éditable : on montre le sujet réel. `{numero}` est
    // remplacé par le numéro de facture définitif au moment de l'émission.
    return "Facture {numero} — Car Maintenance Meyrin";
  };

  // iter268 — Texte de l'email prérempli pour pouvoir le relire et le corriger
  const buildDefaultBody = () => (
    "Bonjour,\n\n"
    + "Veuillez trouver ci-joint votre facture n° {numero}.\n\n"
    + "Cordialement,\nCar Maintenance Meyrin"
  );
  const [busy, setBusy] = useState(null);
  // mailModal.pendingBody : corps de requête à rejouer avec email_override (cas needs_email)
  // mailModal.subject : sujet éditable (iter173, utile pour envoi assurance)
  const [mailModal, setMailModal] = useState({ open: false, email: "", subject: "", body: "", attachments: [], pendingBody: null });
  const [hybride, setHybride] = useState({ open: false, montant: "", canal: null, email: "" });
  // iter159 — Destinataire de la facture (peut différer du client de l'OR)
  const [destinataire, setDestinataire] = useState(null); // null = client OR par défaut. {id, label, email} sinon.

  // iter237 — Adresses proposées (client, facturation, assurance…) dédoublonnées.
  // Si un autre destinataire a été choisi à l'émission, son adresse passe en tête.
  const emailChoices = useMemo(() => {
    const out = [];
    const seen = new Set();
    const add = (email, label) => {
      const e = String(email || "").trim();
      if (!e || !e.includes("@") || seen.has(e.toLowerCase())) return;
      seen.add(e.toLowerCase());
      out.push({ email: e, label });
    };
    if (destinataire?.email) add(destinataire.email, destinataire.label || "destinataire");
    (emailOptions || []).forEach((o) => add(o.email, o.label));
    add(clientEmail, "client");
    return out;
  }, [destinataire, emailOptions, clientEmail]);
  const [destPicker, setDestPicker] = useState({ open: false, q: "", results: [], loading: false });
  // iter164.3 — Popover paiement échelonné (Courrier ou Mail)
  const [installments, setInstallments] = useState({ open: false, action: null, n: 1, days: 30 });

  // Recherche de clients (debounced)
  useEffect(() => {
    if (!destPicker.open) return;
    const q = (destPicker.q || "").trim();
    if (q.length < 2) { setDestPicker((p) => ({ ...p, results: [], loading: false })); return; }
    let alive = true;
    setDestPicker((p) => ({ ...p, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients", { params: { q, limit: 15 } });
        if (!alive) return;
        const items = (data?.items || data || []).filter((c) => c.id !== clientId);
        setDestPicker((p) => ({ ...p, results: items, loading: false }));
      } catch {
        if (alive) setDestPicker((p) => ({ ...p, loading: false }));
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [destPicker.q, destPicker.open, clientId]);

  const buildBody = (base) => {
    if (destinataire?.id) return { ...base, destinataire_client_id: destinataire.id };
    return base;
  };

  const run = async (body, busyKey) => {
    if (busy) return;
    setBusy(busyKey);
    try {
      if (beforeAction) {
        const ok = await beforeAction();
        if (ok === false) { setBusy(null); return; }
      }
      const { data } = await api.post(`/or/${orId}/invoice-action`, buildBody(body));
      if (data.status === "needs_email") {
        setMailModal({
          open: true, email: "", subject: buildDefaultSubject(), body: buildDefaultBody(),
          attachments: [], pendingBody: body,
        });
        return;
      }
      if (data.download_url) {
        await downloadAuthBlob(data.download_url, data.filename || `${data.facture_numero}.pdf`)
          .catch(() => toast.error("Erreur de téléchargement du PDF"));
      }
      toast.success(
        `Facture ${data.facture_numero} émise — ${ACTION_LABELS[body.action] || body.action}` +
        (destinataire?.id ? ` · au nom de ${destinataire.label}` : ""),
        { duration: 5000 },
      );
      setMailModal({ open: false, email: "", subject: "", body: "", attachments: [], pendingBody: null });
      setHybride({ open: false, montant: "", canal: null, email: "" });
      onEmitted?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'émission", { duration: 6000 });
    } finally {
      setBusy(null);
    }
  };

  const handleCaisse = () => {
    if (!window.confirm(
      `Émettre la facture (${Number(total || 0).toFixed(2)} CHF) et l'envoyer en Caisse ?\n\n` +
      "Le client paiera au comptoir (espèces / CB / TWINT)."
    )) return;
    run({ action: "caisse" }, "caisse");
  };

  const handleMail = () => {
    // iter164.3 — Ouvre d'abord le popover échelonné (Mail)
    setInstallments({ open: true, action: "mail", n: 1, days: 30 });
  };

  // iter230 — pièces jointes (base64) pour l'email d'émission
  const onPickMailFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const added = [];
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name} dépasse 10 Mo`);
        continue;
      }
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1] || "");
        r.onerror = reject;
        r.readAsDataURL(f);
      });
      added.push({ filename: f.name, mime: f.type || "application/octet-stream", content_b64: b64, size: f.size });
    }
    setMailModal((p) => ({ ...p, attachments: [...(p.attachments || []), ...added] }));
    e.target.value = "";
  };

  const submitMail = () => {
    const email = (mailModal.email || "").trim();
    if (!email || !email.includes("@")) {
      toast.error("Email invalide");
      return;
    }
    const base = mailModal.pendingBody || { action: "mail" };
    const extras = { email_override: email };
    // iter173 — Sujet personnalisé (assurance : N° sinistre, ou tout autre libre)
    const subj = (mailModal.subject || "").trim();
    if (subj) extras.subject_override = subj;
    // iter230 — message et pièces jointes personnalisés
    const msg = (mailModal.body || "").trim();
    if (msg) {
      extras.body_override = msg
        .split(/\n\s*\n/)
        .map((para) => `<p>${para.replace(/\n/g, "<br/>")}</p>`)
        .join("\n");
    }
    if ((mailModal.attachments || []).length > 0) {
      extras.extra_attachments = mailModal.attachments.map(({ filename, mime, content_b64 }) => (
        { filename, mime, content_b64 }
      ));
    }
    run({ ...base, ...extras }, base.action);
  };

  // iter164.3 — Ouvre le popover échelonné (Courrier)
  const handleCourrier = () => {
    setInstallments({ open: true, action: "courrier", n: 1, days: 30 });
  };

  // iter164.3 — Validation du popover échelonné → suite du flux (Mail ou Courrier)
  const submitInstallments = () => {
    const { action, n, days } = installments;
    const bodyExtras = {};
    if (n > 1) {
      bodyExtras.installments = n;
      bodyExtras.installment_days = days;
    }
    setInstallments({ open: false, action: null, n: 1, days: 30 });
    if (action === "courrier") {
      run({ action: "courrier", ...bodyExtras }, "courrier");
    } else if (action === "mail") {
      const emailPrefilled = destinataire?.email || clientEmail || "";
      // Dossier assurance : la facture est au nom du client mais part à l'assureur
      // → on pré-remplit son adresse (modifiable) plutôt que de laisser vide.
      const emailStart = isAssurance
        ? (destinataire?.email || emailChoices[0]?.email || "")
        : emailPrefilled;
      setMailModal({
        open: true,
        email: emailStart,
        subject: buildDefaultSubject(),
        body: buildDefaultBody(),
        attachments: [],
        pendingBody: { action: "mail", ...bodyExtras },
      });
    }
  };

  const hybMontant = parseFloat(hybride.montant);
  const totalNum = Number(total || 0);
  const hybValid = !isNaN(hybMontant) && hybMontant > 0 && hybMontant < totalNum - 0.05 && !!hybride.canal;
  const hybReste = !isNaN(hybMontant) && hybMontant > 0 ? Math.max(0, totalNum - hybMontant) : null;

  const submitHybride = () => {
    if (!hybValid) return;
    const body = {
      action: "hybride",
      secondary_action: hybride.canal,
      montant_partiel: Number(hybMontant.toFixed(2)),
    };
    if (hybride.canal === "mail" && (hybride.email || "").trim()) {
      body.email_override = hybride.email.trim();
    }
    run(body, "hybride");
  };

  const btn = (key, label, Icon, onClick, cls) => (
    <Button
      key={key}
      disabled={!!busy}
      onClick={onClick}
      data-testid={`emission-btn-${key}`}
      className={`h-auto py-3 flex-col gap-1 ${cls}`}
    >
      {busy === key ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5" />}
      <span className="text-xs font-semibold">{label}</span>
    </Button>
  );

  return (
    <>
      <div
        className="rounded-xl border-2 border-[#52a675]/30 bg-gradient-to-br from-[#f0f9f4] to-white p-4 sm:p-5"
        data-testid="emission-actions-panel"
      >
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
            <Send className="w-5 h-5 text-[#52a675]" /> Émettre la facture
          </h3>
          <div className="text-sm text-gray-600">
            Total : <span className="font-mono font-bold text-gray-900">{totalNum.toFixed(2)} CHF</span>
          </div>
        </div>

        {/* iter159 — Destinataire de la facture */}
        <div
          className={`mb-3 rounded-lg border p-2.5 flex items-center justify-between gap-2 ${
            destinataire ? "bg-amber-50 border-amber-300" : "bg-white border-gray-200"
          }`}
          data-testid="emission-destinataire-block"
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <UserCog className={`w-4 h-4 flex-shrink-0 ${destinataire ? "text-amber-700" : "text-gray-500"}`} />
            <div className="text-xs sm:text-sm min-w-0 flex-1">
              <div className="text-gray-500 leading-tight">Destinataire de la facture</div>
              <div className={`font-semibold truncate ${destinataire ? "text-amber-900" : "text-gray-900"}`} data-testid="emission-destinataire-current">
                {destinataire ? destinataire.label : (clientLabel || "Client de l'OR")}
                {destinataire && <span className="ml-1.5 text-[10px] font-normal text-amber-700">(modifié)</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {destinataire && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDestinataire(null)}
                className="h-8 px-2 text-amber-700 hover:bg-amber-100"
                data-testid="emission-destinataire-reset"
                title="Revenir au client de l'OR"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDestPicker({ open: true, q: "", results: [], loading: false })}
              className="h-8 text-xs"
              data-testid="emission-destinataire-change"
              disabled={!!busy}
            >
              <UserCog className="w-3.5 h-3.5 mr-1" /> Changer
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {btn("caisse", "Caisse", Banknote, handleCaisse, "bg-emerald-600 hover:bg-emerald-700 text-white")}
          {btn("mail", "Mail", Mail, handleMail, "bg-blue-600 hover:bg-blue-700 text-white")}
          {btn("courrier", "Courrier", Printer, handleCourrier, "bg-gray-700 hover:bg-gray-800 text-white")}
          {btn("hybride", "Hybride", Shuffle, () => setHybride({ open: true, montant: "", canal: null, email: destinataire?.email || clientEmail || "" }), "bg-amber-600 hover:bg-amber-700 text-white col-span-2 sm:col-span-1")}
        </div>
        <p className="text-[11px] text-gray-500 mt-2.5">
          Le numéro de facture (RE-XXXXX) et les écritures comptables sont créés au moment de l&apos;émission.
          Courrier = téléchargement du PDF (nommé n° de facture) pour impression.
        </p>
      </div>

      {/* iter164.3 — Popover Paiement échelonné (Courrier / Mail) */}
      <Dialog open={installments.open} onOpenChange={(open) => !open && setInstallments({ open: false, action: null, n: 1, days: 30 })}>
        <DialogContent data-testid="emission-installments-modal">
          <DialogHeader>
            <DialogTitle>Paiement échelonné · {installments.action === "mail" ? "Envoi par email" : "Impression courrier"}</DialogTitle>
            <DialogDescription>
              Choisissez en combien de tranches le client peut régler cette facture. Un QR-bill sera généré par tranche, chacun avec sa propre échéance.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm flex items-center justify-between">
              <span className="text-gray-600">Montant total</span>
              <span className="font-mono font-semibold" data-testid="installments-total">{Number(total || 0).toFixed(2)} CHF</span>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Nombre de tranches</label>
              <div className="grid grid-cols-5 gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setInstallments((p) => ({ ...p, n }))}
                    className={`px-2 py-2 rounded-md border text-sm font-semibold transition ${installments.n === n ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                    data-testid={`installments-n-${n}`}
                  >
                    {n}×
                  </button>
                ))}
              </div>
            </div>
            {installments.n > 1 && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Délai entre tranches (jours)</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {[10, 15, 30, 60, 90].map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setInstallments((p) => ({ ...p, days: d }))}
                        className={`px-3 py-1.5 rounded-md border text-sm transition ${installments.days === d ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                        data-testid={`installments-days-${d}`}
                      >
                        {d} j
                      </button>
                    ))}
                    <Input
                      type="number" min={1} max={365}
                      value={installments.days}
                      onChange={(e) => setInstallments((p) => ({ ...p, days: Math.max(1, Math.min(365, parseInt(e.target.value || "30", 10))) }))}
                      className="h-9 w-20 text-sm"
                      data-testid="installments-days-input"
                    />
                  </div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm space-y-1" data-testid="installments-preview">
                  <div className="text-xs uppercase tracking-wide text-emerald-800 font-semibold mb-1">Aperçu des {installments.n} tranches</div>
                  {(() => {
                    const totalN = Number(total || 0);
                    const base = Math.round((totalN / installments.n) * 100) / 100;
                    const first = Math.round((totalN - base * (installments.n - 1)) * 100) / 100;
                    const amounts = [first, ...Array(installments.n - 1).fill(base)];
                    const today = new Date();
                    return amounts.map((a, i) => {
                      const due = new Date(today);
                      due.setDate(today.getDate() + (i + 1) * installments.days);
                      return (
                        <div key={i} className="flex justify-between text-sm">
                          <span>Tranche {i + 1}/{installments.n} — échéance {due.toLocaleDateString("fr-CH")}</span>
                          <span className="font-mono font-semibold">{a.toFixed(2)} CHF</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInstallments({ open: false, action: null, n: 1, days: 30 })} data-testid="installments-cancel">Annuler</Button>
            <Button onClick={submitInstallments} className={installments.action === "mail" ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-700 hover:bg-gray-800"} data-testid="installments-submit">
              {installments.n === 1
                ? (installments.action === "mail" ? "Continuer vers l'email" : "Générer le PDF")
                : `Générer ${installments.n} QR-bills`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal email (mail direct ou email manquant) */}
      <Dialog
        open={mailModal.open}
        onOpenChange={(open) => setMailModal((p) => ({ ...p, open }))}
      >
        <DialogContent data-testid="emission-email-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Envoyer la facture par email
              {isAssurance && (
                <span
                  className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300"
                  data-testid="emission-email-assurance-badge"
                >
                  Assurance
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {isAssurance
                ? "Facture au nom du client, envoyée à l'assurance : vérifie l'adresse email et l'objet (N° de sinistre). L'email ne sera pas enregistré sur la fiche client."
                : (mailModal.pendingBody
                  ? "Aucun email n'est enregistré pour ce client. Saisissez-le : il sera sauvegardé sur la fiche client."
                  : "L'email sera enregistré sur la fiche client s'il est modifié.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                Adresse email
              </Label>
              <Input
                value={mailModal.email}
                onChange={(e) => setMailModal((p) => ({ ...p, email: e.target.value }))}
                type="email"
                placeholder={isAssurance ? "sinistres@assurance.ch" : "client@exemple.ch"}
                data-testid="emission-email-input"
                autoFocus
                className="mt-1"
              />
              {/* iter237 — Plusieurs adresses connues : choix en un clic */}
              {emailChoices.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="emission-email-options">
                  {emailChoices.map((opt, i) => {
                    const active = opt.email.toLowerCase() === (mailModal.email || "").trim().toLowerCase();
                    return (
                      <button
                        key={opt.email}
                        type="button"
                        onClick={() => setMailModal((p) => ({ ...p, email: opt.email }))}
                        className={`px-2 py-1 rounded-full border text-xs transition-colors ${
                          active
                            ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                            : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                        title={opt.label}
                        data-testid={`emission-email-option-${i}`}
                      >
                        {opt.email}
                        <span className="ml-1 text-[10px] opacity-70">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                Objet de l&apos;email {isAssurance && <span className="text-amber-700 normal-case">(recommandé : N° de sinistre)</span>}
              </Label>
              <Input
                value={mailModal.subject}
                onChange={(e) => setMailModal((p) => ({ ...p, subject: e.target.value }))}
                placeholder={isAssurance
                  ? "Facture RE-XXXXX — N° sinistre …"
                  : "Laisser vide pour utiliser le sujet par défaut"}
                data-testid="emission-email-subject"
                className="mt-1"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                <code className="bg-gray-100 px-1 rounded">{"{numero}"}</code> est remplacé par le numéro
                de facture définitif à l&apos;émission.
              </p>
            </div>
            {/* iter230 — Message et pièces jointes personnalisables */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                Texte de l&apos;email
              </Label>
              <Textarea
                rows={7}
                value={mailModal.body}
                onChange={(e) => setMailModal((p) => ({ ...p, body: e.target.value }))}
                placeholder="Laisser vide pour le texte standard (facture en pièce jointe, QR-bill, délai de paiement)…"
                className="mt-1 text-sm"
                data-testid="emission-email-body"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                Pièces jointes supplémentaires
              </Label>
              <input
                type="file"
                multiple
                className="text-xs mt-1 block"
                onChange={onPickMailFiles}
                data-testid="emission-email-file-input"
              />
              {(mailModal.attachments || []).length > 0 && (
                <ul className="mt-2 space-y-1">
                  {mailModal.attachments.map((a, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs bg-gray-50 rounded px-2 py-1" data-testid={`emission-email-attachment-${i}`}>
                      <Paperclip className="w-3 h-3" />
                      <span className="flex-1 truncate">{a.filename}</span>
                      <span className="text-gray-500">{Math.round(a.size / 1024)} Ko</span>
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-800"
                        onClick={() => setMailModal((p) => ({ ...p, attachments: p.attachments.filter((_, j) => j !== i) }))}
                        data-testid={`emission-email-attachment-remove-${i}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-[11px] text-gray-500 mt-1">
                Le PDF de la facture est joint automatiquement. Max 10 Mo par fichier.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMailModal({ open: false, email: "", subject: "", body: "", attachments: [], pendingBody: null })}
              data-testid="emission-email-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={submitMail}
              disabled={!!busy}
              className={isAssurance ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-600 hover:bg-blue-700"}
              data-testid="emission-email-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              {isAssurance ? "Envoyer à l'assurance" : "Envoyer la facture"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal hybride */}
      <Dialog
        open={hybride.open}
        onOpenChange={(open) => setHybride((p) => ({ ...p, open }))}
      >
        <DialogContent data-testid="emission-hybride-modal">
          <DialogHeader>
            <DialogTitle>Émission hybride</DialogTitle>
            <DialogDescription>
              Une partie est encaissée au comptoir (Caisse), le solde part par le canal choisi
              avec un QR-bill recalculé sur le reste.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm flex justify-between">
              <span className="text-gray-600">Total facture</span>
              <span className="font-mono font-semibold">{totalNum.toFixed(2)} CHF</span>
            </div>
            <div>
              <Label className="text-sm">Montant à encaisser au comptoir *</Label>
              <Input
                type="number"
                step="0.05"
                min="0"
                inputMode="decimal"
                value={hybride.montant}
                onChange={(e) => setHybride((p) => ({ ...p, montant: e.target.value }))}
                placeholder="Ex: 300.00"
                className="font-mono mt-1"
                data-testid="emission-hybride-montant"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-sm">Canal pour le solde *</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {SECONDARY_OPTIONS.map(({ key, label, Icon }) => (
                  <Button
                    key={key}
                    type="button"
                    variant={hybride.canal === key ? "default" : "outline"}
                    onClick={() => setHybride((p) => ({ ...p, canal: key }))}
                    data-testid={`emission-hybride-canal-${key}`}
                    className={`flex-col h-auto py-2.5 ${hybride.canal === key ? "bg-amber-600 hover:bg-amber-700" : "border-amber-300 hover:bg-amber-50"}`}
                  >
                    <Icon className="w-4 h-4 mb-0.5" />
                    <span className="text-xs">{label}</span>
                  </Button>
                ))}
              </div>
            </div>
            {hybride.canal === "mail" && (
              <div>
                <Label className="text-sm">Email du client</Label>
                <Input
                  type="email"
                  value={hybride.email}
                  onChange={(e) => setHybride((p) => ({ ...p, email: e.target.value }))}
                  placeholder="client@exemple.ch"
                  className="mt-1"
                  data-testid="emission-hybride-email"
                />
              </div>
            )}
            {hybReste !== null && (
              <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="emission-hybride-recap">
                <div className="flex justify-between text-sm">
                  <span className="text-amber-800">À encaisser au comptoir</span>
                  <span className="font-mono">{hybMontant.toFixed(2)} CHF</span>
                </div>
                <div className="flex justify-between text-base font-bold text-amber-900 mt-1 pt-1 border-t border-amber-300">
                  <span>Solde ({hybride.canal ? ACTION_LABELS[hybride.canal] : "canal à choisir"})</span>
                  <span className="font-mono">{hybReste.toFixed(2)} CHF</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setHybride({ open: false, montant: "", canal: null, email: "" })}
              data-testid="emission-hybride-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={submitHybride}
              disabled={!hybValid || !!busy}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="emission-hybride-submit"
            >
              {busy === "hybride" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Shuffle className="w-4 h-4 mr-1.5" />}
              Émettre (hybride)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* iter159 — Sélecteur de destinataire (client existant) */}
      <Dialog
        open={destPicker.open}
        onOpenChange={(open) => setDestPicker((p) => ({ ...p, open }))}
      >
        <DialogContent data-testid="emission-destinataire-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Choisir le destinataire de la facture</DialogTitle>
            <DialogDescription>
              Sélectionne un client existant à qui adresser la facture (leasing, assurance, société flotte, etc.).
              L&apos;OR sera ré-attaché à ce nouveau client.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={destPicker.q}
              onChange={(e) => setDestPicker((p) => ({ ...p, q: e.target.value }))}
              placeholder="Rechercher (nom, raison sociale, plaque...)"
              className="pl-9"
              data-testid="emission-destinataire-search"
              autoFocus
            />
          </div>
          <div className="max-h-72 overflow-y-auto border rounded-lg divide-y divide-gray-100">
            {destPicker.loading && (
              <div className="p-3 text-center text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> Recherche...
              </div>
            )}
            {!destPicker.loading && destPicker.q.trim().length < 2 && (
              <div className="p-4 text-center text-xs text-gray-400">Tape au moins 2 caractères pour rechercher.</div>
            )}
            {!destPicker.loading && destPicker.q.trim().length >= 2 && destPicker.results.length === 0 && (
              <div className="p-4 text-center text-xs text-gray-500">Aucun client trouvé.</div>
            )}
            {destPicker.results.map((c) => {
              const label = c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim() || "(sans nom)";
              const sub = [c.localite, c.tel_principal, c.email].filter(Boolean).join(" · ");
              return (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => {
                    setDestinataire({ id: c.id, label, email: c.email || "" });
                    setDestPicker({ open: false, q: "", results: [], loading: false });
                  }}
                  className="w-full text-left p-3 hover:bg-amber-50 transition-colors"
                  data-testid={`emission-destinataire-option-${c.id}`}
                >
                  <div className="font-semibold text-sm text-gray-900 flex items-center gap-1.5">
                    {label}
                    {c.type === "entreprise" && (
                      <span className="text-[9px] text-blue-700 bg-blue-50 border border-blue-200 px-1 py-0.5 rounded">Entreprise</span>
                    )}
                    {c.type === "assurance" && (
                      <span className="text-[9px] text-violet-700 bg-violet-50 border border-violet-200 px-1 py-0.5 rounded">Assurance</span>
                    )}
                  </div>
                  {sub && <div className="text-[11px] text-gray-500 truncate">{sub}</div>}
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDestPicker({ open: false, q: "", results: [], loading: false })}
              data-testid="emission-destinataire-cancel"
            >
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
